import { createElement, updateElement } from '../../global/ui-helpers';
import { UIBadge } from '../badge/ui-badge';
import { UITabs } from './ui-tabs';
import { BrandColors } from '../../global/helpers-marketing';
import type { UIIcon } from '../icon/ui-icon';
import type { ElementConfig } from '../ui-element.type';
import type { ScrollContext } from '../../global/utils.type';

/**
 * Create element to store label for tab's badge.
 * @param {string} label Label for tab's badge.
 * @returns {HTMLElement | null}
 * @private
 */
export function buildTabsBadge(label: string): UIBadge | null {
    return createElement({
        tagName: 'ui-badge',
        attributes: {
            color: BrandColors.Orange,
            layout: UIBadge.Layout.Pin,
        },
        children: [document.createTextNode(label)],
    });
}

/**
 * Builds icon for the tab ("new window icon on the right side).
 * @param {string} glyph Icon code.
 * @returns {HTMLElement | null}
 * @private
 */
export function buildIcon(glyph: string): UIIcon | null {
    return createElement({
        tagName: 'ui-icon',
        attributes: {
            glyph: glyph,
        },
    });
}

/**
 * Renders scroll button
 * @param {'left' | 'right'} direction
 * @param {string} controlsId Id of element the scroll buttons control
 * @param {string} element
 * @returns {ElementConfig}
 */
export function renderButton(
    direction: 'left' | 'right',
    controlsId: string,
    element: string = 'ui-tabs'
): ElementConfig {
    const icon: ElementConfig = {
        tagName: 'ui-icon',
        attributes: {
            glyph: direction,
        },
        classList: {
            '-navicon': true,
        },
    };

    const button: ElementConfig = {
        tagName: 'button',
        attributes: {
            'aria-label': UITabs.labels[direction],
            'aria-controls': controlsId,
            'type': 'button',
        },
        classList: {
            'button': true,
            '-guiding': true,
        },
        children: [icon],
    };

    return {
        tagName: 'div',
        attributes: {
            placement: direction,
        },
        classList: {
            [`${element}__scroll-container`]: true,
        },
        children: [button],
    };
}

/**
 * Handles click event for scroll button.
 * @param {PointerEvent} event
 * @param {ScrollContext} scrollContext - Context object containing scroll elements
 * @param {string} containerElement
 * @private
 */
export function handleScrollClickEvent(
    event: MouseEvent,
    scrollContext: ScrollContext,
    containerElement: string = 'ui-tabs'
): void {
    const { captions, scrollButtonLeft, scrollButtonRight } = scrollContext;
    if (!scrollButtonLeft || !scrollButtonRight) {
        return;
    }

    const button = (event.target as HTMLElement).closest(
        `.${containerElement}__scroll-container`
    );
    if (!button) {
        return;
    }
    const direction = button.getAttribute('placement');

    const overscrollPxAmount = 4; // extra pixels to scroll to fully reveal the tab. looks better visually when the scroll button is not directly against the tab edge.

    const leftButtonBox = scrollButtonLeft.getBoundingClientRect();
    const rightButtonBox = scrollButtonRight.getBoundingClientRect();
    const childrenRects = Array.from(captions!.children).map((elem) =>
        elem.getBoundingClientRect()
    );

    if (direction === 'left') {
        // find the leftmost tab's rect that is at least partially hidden by the left scroll button
        const rect = childrenRects
            .reverse()
            .find((r) => r.left <= leftButtonBox.right);
        if (rect) {
            captions!.scrollBy(
                rect.left - leftButtonBox.right - overscrollPxAmount,
                0
            );
        }
    } else if (direction === 'right') {
        // find the rightmost tab's rect that is at least partially hidden by the right scroll button
        const rect = childrenRects.find((r) => r.right >= rightButtonBox.left);
        if (rect) {
            captions!.scrollBy(
                rect.right - rightButtonBox.left + overscrollPxAmount,
                0
            );
        }
    }
}

/**
 * Handles scroll button visibility in label list.
 * @param {ScrollContext} scrollContext - Context object containing scroll elements
 * @private
 */
export function handleScrollButtonVisibility(
    scrollContext: ScrollContext
): void {
    const { captions, scrollButtonLeft, scrollButtonRight } = scrollContext;
    if (!scrollButtonLeft || !scrollButtonRight) {
        return;
    }

    const shouldHideLeftButton = !captions?.scrollLeft;
    if (scrollButtonLeft.parentElement) {
        updateElement(scrollButtonLeft.parentElement, {
            classList: { 'scroll-hidden': shouldHideLeftButton },
        });
    }
    updateElement(scrollButtonLeft, {
        attributes: {
            'disabled': shouldHideLeftButton,
            'aria-disabled': shouldHideLeftButton,
        },
    });

    const pxHiddenByScroll = captions!.scrollWidth - captions!.clientWidth;
    // account for sub-pixel layout / fractional scrollLeft values:
    // treat the right button as hidden when we're within 1px of the end
    const shouldHideRightButton = pxHiddenByScroll - captions!.scrollLeft <= 1;
    if (scrollButtonRight.parentElement) {
        updateElement(scrollButtonRight.parentElement, {
            classList: { 'scroll-hidden': shouldHideRightButton },
        });
    }
    updateElement(scrollButtonRight, {
        attributes: {
            'disabled': shouldHideRightButton,
            'aria-disabled': shouldHideRightButton,
        },
    });
}
